import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useToastStore, type ToastItem } from "./toastStore";

/**
 * One message. Its time stops while the pointer is over it or focus is inside it, so a person who is reading it or reaching for
 * its link does not lose it (UI-27); the time that was left runs on afterwards.
 */
function Toast({ toast, onDismiss }: { toast: ToastItem; onDismiss: (id: number) => void }) {
  const [held, setHeld] = useState(false);
  const left = useRef(toast.timeoutMs);
  useEffect(() => {
    if (toast.timeoutMs <= 0 || held) return;
    const started = Date.now();
    const timer = setTimeout(() => onDismiss(toast.id), left.current);
    return () => {
      clearTimeout(timer);
      left.current = Math.max(1, left.current - (Date.now() - started));
    };
  }, [toast.id, toast.timeoutMs, held, onDismiss]);

  return (
    <li
      className="toast"
      data-tone={toast.tone}
      role={toast.tone === "warning" ? "alert" : "status"}
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)}
      onBlur={() => setHeld(false)}
    >
      <span className="toast__text">{toast.text}</span>
      {toast.action && (
        <Link className="toast__action" to={toast.action.to} onClick={() => onDismiss(toast.id)}>
          {toast.action.label}
        </Link>
      )}
      <button type="button" className="icon-button toast__close" aria-label="Dismiss this message" title="Dismiss" onClick={() => onDismiss(toast.id)}>
        <span aria-hidden="true">×</span>
      </button>
    </li>
  );
}

/** Where the short messages appear. The shell draws it once. */
export function ToastHost() {
  const toasts = useToastStore((state) => state.toasts);
  const dismiss = useToastStore((state) => state.dismiss);
  return (
    <ul className="toast-host" aria-label="Messages">
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onDismiss={dismiss} />
      ))}
    </ul>
  );
}
